import { HttpClient } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {url} from '../helpers/url';
import {SharedSettings} from '../models/shared-settings';
import {Observable, tap} from 'rxjs';
import {CreatedTrailer, Trailer, TrailerChecklistCheck, TrailerStatus, TrailerType} from "../models/trailer";
import { buildQueryParams } from '../helpers/url';
import {DateTime} from "luxon";

@Injectable({
    providedIn: 'root',
})
export class TrailerService {


    constructor(
        private http: HttpClient,
        private settings: SharedSettings,
    ) {
    }

    getTrailers(params: { siteId: string, date: DateTime, carrierId?: number, trailerNumber?: string, subinventoryId?: number, hasDeparted?: boolean }): Observable<Trailer[]> {
        const queryParams = buildQueryParams(params);

        return this.http.get<Trailer[]>(this.settings.apiBaseUrl + `trailers?${queryParams.toString()}`)
            .pipe(tap(trailers => trailers.forEach(t => {
                this.fixDates(t);
            })));
    }

    GetFreeTrailers(
        siteId: string,
        subinventoryId: number,
        date: DateTime | null,
        carrierId: number | null,
        statuses: TrailerStatus[],
        currentId: number): Observable<Trailer[]> {

        const statusQuery = statuses.map((s, i) => url`trailerStatus[${i}]=${s}`).join('&');

        return this.http.get<Trailer[]>(this.settings.apiBaseUrl
                + url`trailers/free?siteId=${siteId}&`
                + (date ? url`date=${date.toISODate()}&` : '')
                + (subinventoryId ? url`siteSubinventoryId=${subinventoryId}&` : '')
                + (carrierId ? url`carrierId=${carrierId}&` : '')
                + (currentId ? url`currentId=${currentId}&` : '')
                + statusQuery)
            .pipe(tap(trailers => trailers.forEach(t => {
                this.fixDates(t);
            })));
    }

    getTrailer(trailerId: number): Observable<Trailer> {
        return this.http.get<Trailer>(this.settings.apiBaseUrl + url`trailers/${trailerId}`)
            .pipe(tap(trailer => {
                this.fixDates(trailer);
            }));
    }

    updateTrailerNumber(trailerId: number, trailerNumber: string): Observable<Trailer> {
        return this.http.put<Trailer>(this.settings.apiBaseUrl + url`trailers/${trailerId}/trailerNumber`, JSON.stringify(trailerNumber), {
            headers: {
                'Content-Type': 'application/json',
            },
        }).pipe(tap(trailer => this.fixDates(trailer)));
    }

    updateTrailerTypeId(trailerId: number, trailerTypeId: number): Observable<Trailer> {
        return this.http.put<Trailer>(this.settings.apiBaseUrl + url`trailers/${trailerId}/trailerTypeId`, JSON.stringify(trailerTypeId), {
            headers: {
                'Content-Type': 'application/json',
            },
        }).pipe(tap(trailer => this.fixDates(trailer)));
    }

    CreateTrailer(trailer: CreatedTrailer): Observable<Trailer> {
        return this.http.post<Trailer>(this.settings.apiBaseUrl + 'trailers', trailer);
    }

    arriveTrailer(trailerId: number, datetime: DateTime): Observable<Trailer> {
        return this.http.patch<Trailer>(this.settings.apiBaseUrl + url`trailers/${trailerId}/arrive`, datetime);
    }

    departTrailer(trailerId: number, datetime: DateTime): Observable<Trailer> {
        return this.http.patch<Trailer>(this.settings.apiBaseUrl + url`trailers/${trailerId}/depart`, datetime);
    }

    deleteTrailer(trailerId: number): Observable<void> {
        return this.http.delete<void>(this.settings.apiBaseUrl + url`trailers/${trailerId}`);
    }

    getTrailerTypes(): Observable<TrailerType[]> {
        return this.http.get<TrailerType[]>(this.settings.apiBaseUrl + 'trailers/types');
    }

    /**
     * Gets the trailer checklist checks for the worked shift
     * @param workedShiftId
     */
    getChecklist(workedShiftId: number): Observable<TrailerChecklistCheck[]> {
        return this.http.get<TrailerChecklistCheck[]>(this.settings.apiBaseUrl + url`trailers/checklist/${workedShiftId}`)
            .pipe(tap(checks => {
                checks.forEach(check => {
                    this.fixChecklistCheckDates(check);
                });
            }));
    }

    /**
     * Marks a trailer with a checklist check for the worked shift
     * @param trailerId
     * @param workedShiftId
     * @param check
     */
    checkTrailer(trailerId: number, workedShiftId: number, check: boolean): Observable<TrailerChecklistCheck> {
        return this.http.put<TrailerChecklistCheck>(this.settings.apiBaseUrl + url`trailers/checklist/${workedShiftId}/${trailerId}`, {
            Checked: check
        }).pipe(tap(this.fixChecklistCheckDates));
    }

    /**
     * Clears all trailer checklist checks for the worked shift
     * @param workedShiftId
     */
    resetTrailerChecklist(workedShiftId: number): Observable<void> {
        return this.http.delete<void>(this.settings.apiBaseUrl + url`trailers/checklist/${workedShiftId}`);
    }

    private fixChecklistCheckDates(check: TrailerChecklistCheck) {
        check.lastChanged = DateTime.fromISO(check.lastChanged.toString());
    }

    private fixDates(trailer: Trailer): void {
        trailer.arrivedAt = !!trailer.arrivedAt ? DateTime.fromISO(trailer.arrivedAt?.toString()) : trailer.arrivedAt;
        trailer.departedAt = !!trailer.departedAt ? DateTime.fromISO(trailer.departedAt?.toString()) : trailer.departedAt;
        trailer.loadedAt = !!trailer.loadedAt ? DateTime.fromISO(trailer.loadedAt?.toString()) : trailer.loadedAt;
        trailer.unloadedAt = !!trailer.unloadedAt ? DateTime.fromISO(trailer.unloadedAt?.toString()) : trailer.unloadedAt;
    }
}
